import type { AddressBookVariable } from '@integrations/address-books';
import type { CrmDeal } from '@integrations/crm-deals';
import type { CrmContact } from '@integrations/crm-contacts';

import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, take } from 'rxjs';
import { filter } from 'rxjs/operators';

import type { ESocialNetworks } from '@common/enums';
import { environment } from '@web-env/environment';

import type { FormElementVariableData, FormWidgetElement } from '@sites/entity-core';
import type { FormWidgetForm } from '@sites/entity-signal-form';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { ChatBotsCollectionFacade } from '@web/store/collections/chat-bots/chat-bots.facade';

import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import type { ChatBotVariableChange } from '@components/sp-form-element-settings-signal';
import { SpFormElementSettingsSignalComponent } from '@components/sp-form-element-settings-signal';

import { FormSettingsPanel } from '@web/widgets/common/abstract/form-settings-panel';

/**
 * Breadcrumbs/footer chrome around `sp-form-element-settings-signal`, editing
 * one element of the widget form's `elements` array in place
 * (`form.updateElement(index, …)` / whole-element replacement on type
 * change). Chat-bot variables are loaded via forkJoin over the bots' variable
 * collections; the bot variable picked for this field is also mirrored onto
 * the matching `chatBots` item. Save/cancel and the transaction frame come
 * from `FormSettingsPanel`.
 */
@Component({
    selector: 'web-form-element-settings',
    templateUrl: './form-element-settings.component.html',
    styleUrls: ['./form-element-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, SpFormElementSettingsSignalComponent, AsyncPipe],
})
export class FormElementSettingsComponent extends FormSettingsPanel<FormWidgetForm> {
    public readonly index = input.required<number>();
    public readonly addChatBotToForm = input<boolean>(false);

    public readonly addressBookVariablesList = input<AddressBookVariable[]>([]);
    public readonly crmContactsVariablesList = input<CrmContact[]>([]);
    public readonly crmDealsVariablesList = input<CrmDeal[]>([]);

    public readonly isAddressBookListDisabled = input<boolean>(false);

    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly chatBotsCollectionFacade: ChatBotsCollectionFacade = inject(ChatBotsCollectionFacade);

    public readonly baseHref = environment.baseHref;
    public readonly selectedDeviceMode$ = this.layoutFacade.deviceMode$;

    public readonly variablesOptions = signal<FormElementVariableData[][]>([]);
    public readonly isInitVariables = signal(false);

    public readonly element = computed<FormWidgetElement | undefined>(
        () => this.form().elements().value()[this.index()]
    );
    public readonly elements = computed<FormWidgetElement[]>(() => this.form().elements().value());
    public readonly chatBots = computed(() => this.form().chatBots().value());

    public override ngOnInit(): void {
        super.ngOnInit();

        if (this.addChatBotToForm()) {
            this.loadChatBotVariables();
        }
    }

    public onElementChange(value: FormWidgetElement): void {
        // Whole-element write: covers both per-field patches and the full
        // replacement a type change produces.
        this.form().updateElement(this.index(), value);
    }

    public onChatBotVariableChange({ socialNetwork, variable }: ChatBotVariableChange): void {
        const index = this.chatBots().findIndex((bot) => bot.socialNetwork === socialNetwork);
        if (index === -1) return;
        this.form().updateChatBot(index, { variables: variable });
    }

    private loadChatBotVariables(): void {
        const bots = this.chatBots();

        const requests = bots.map((bot) =>
            this.chatBotsCollectionFacade.chatBotVariables$(bot.socialNetwork as ESocialNetworks, bot.botId).pipe(
                filter((res) => res !== undefined),
                take(1)
            )
        );

        forkJoin(requests)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((allVars) => {
                this.variablesOptions.set(allVars as FormElementVariableData[][]);
                this.isInitVariables.set(true);
            });
    }
}
